import { getFlag, useFlags } from '@nx/launch-darkly-service';
import {
  type RootState as FrameworkRootState,
  StoreKit,
  createDynamicBaseQuery,
  getSharedStorageUrl,
  makeSelectFeatureFlag,
  prepareHeaders,
  selectCapability,
  useCapability,
  useFeatureFlag,
} from '@nx/state';

import { log } from '../../services/logging';
import { customCreateApi } from '../context';
import { perspectiveEndpoints } from './endpoints/perspectives';
import { sceneEndpoints } from './endpoints/scenes';
import { type Builder, tagTypes } from './types';

export const NAME = 'shared-storage-api';

/**
 * Utility function to check if we can use Shared Storage outside of UI,
 * such as in redux.
 */
export const isSharedStorageAvailable = (): boolean => {
  const { globalStore } = StoreKit.UNSAFE_getGlobalStoreInstance();
  const state = globalStore.getState();
  const isCapabilityEnabled = selectCapability(state, { key: 'framework:storage-api' });
  const isLDFeatureFlagEnabled = getFlag('exploreSharedStorage', false);
  const isInternalFeatureFlagEnabled = makeSelectFeatureFlag('explore:shared-storage')(state);

  return isCapabilityEnabled && (isLDFeatureFlagEnabled || isInternalFeatureFlagEnabled);
};

/**
 * React hook to check if we can use Shared Storage
 */
export const useHasSharedStorage = (): boolean => {
  const [isCapabilityEnabled] = useCapability('framework:storage-api');
  const { exploreSharedStorage: isLDFeatureFlagEnabled } = useFlags();
  const [isInternalFeatureFlagEnabled] = useFeatureFlag('explore:shared-storage');

  return isCapabilityEnabled && ((isLDFeatureFlagEnabled ?? false) || isInternalFeatureFlagEnabled);
};

export const sharedStorageApi = customCreateApi({
  reducerPath: NAME,
  tagTypes,
  baseQuery: createDynamicBaseQuery(
    (state: FrameworkRootState) => getSharedStorageUrl(state, log.warn, 'sharedStorageApiUrl'),
    {
      prepareHeaders,
    },
    (): FrameworkRootState => {
      const { globalStore } = StoreKit.UNSAFE_getGlobalStoreInstance();
      return globalStore.getState();
    },
  ),
  endpoints: (builder: Builder) => ({
    ...perspectiveEndpoints(builder),
    ...sceneEndpoints(builder),
  }),
});

export const { useDeletePerspectiveMutation } = sharedStorageApi;
